import { computed } from 'vue';

import { useEVBInputStore } from '@/features/EVBService/useEVBInputStore';
import {
  isAbweichenderHalterCompany,
  isCompany,
} from '@/features/Sparten/shared/renderConditions/kfz/sharedRenderConditions';
import { ROLE, useClientStore } from '@/features/Clients';
import {
  hasVorschadenFeuerBlitz,
  hasVorschadenLeitungswasser,
  hasVorschadenSonstige,
  hasVorschadenSturmHagel,
} from '@/features/Sparten/woh/formConfigs/sharedRenderConditions';
import { useGlobalStore } from '@/features/Global/store';
import { hasPreviousDamage } from '@/features/Contract/Property/utils';
import { useTariffStore } from '@/features/Tariffs/store';
import { useCompaniesStore } from '@/features/Companies/store';

export const allowAbweichenderHalter = computed((): boolean => {
  const { company } = useEVBInputStore();

  if (company === null) {
    return false;
  }

  if (isCompany.value) {
    return company.allowAbweichenderHalterJurist;
  }

  return company.allowAbweichenderHalter;
});

export const isAbweichenderHalterRequired = computed(
  (): boolean =>
    useEVBInputStore().hasAbweichenderHalter && allowAbweichenderHalter.value,
);

export const isVertretungsberechtigt = computed(
  (): boolean =>
    !isCompany.value ||
    (isCompany.value &&
      useEVBInputStore().company !== null &&
      useEVBInputStore().company.requireVertretungsberechtigter),
);

export const isAbweichenderHalterVertretungsberechtigt = computed(
  (): boolean =>
    !isAbweichenderHalterCompany.value ||
    (isAbweichenderHalterCompany.value &&
      useEVBInputStore().company !== null &&
      useEVBInputStore().company.requireVertretungsberechtigter),
);

export const isVertretungsberechtigterRequired = computed((): boolean => {
  if (!isCompany.value) {
    return true;
  }

  return isVertretungsberechtigt.value;
});

export const isAbweichenderHalterVertretungsberechtigterRequired = computed(
  (): boolean => {
    if (!isAbweichenderHalterCompany.value) {
      return true;
    }

    return isAbweichenderHalterVertretungsberechtigt.value;
  },
);

export const vpIsNotVn = computed(
  () =>
    !useClientStore().isSameByRole(
      ROLE.VERSICHERUNGSNEHMER,
      ROLE.VERSICHERTE_PERSON,
    ),
);

export const hasNoStandardVorschaeden = computed(() => {
  if (useGlobalStore().sparte === 'woh') {
    return (
      hasPreviousDamage.value &&
      !hasVorschadenLeitungswasser() &&
      !hasVorschadenSturmHagel() &&
      !hasVorschadenFeuerBlitz() &&
      !hasVorschadenSonstige()
    );
  }

  return false;
});

export const hasMemofeld = computed(() => {
  const companyId = useTariffStore().contractTariff.gesellschaft.id;

  return useCompaniesStore().byInsuranceId[companyId].supports.memo;
});
